// Upstream catalog table for one Chromium major.
//
// Reads from `chromium_upstream_releases` (neutral chromium catalog mirror
// populated by the chromium-catalog-poll cron). Each row is decorated
// with "published" / "Publish" so an operator spots drift without leaving
// the page; the Publish button deep-links into /releases/new/chromium with
// the version pre-filled.
//
// Rendered with the shared @veil/ui DataTable stack; the version column
// sorts by semver.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { DataTable, DataTableColumnHeader } from "@veil/ui/data-table";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import { Loader2, RefreshCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import type { UpstreamRelease } from "@/lib/releases/types.ts";

interface Props {
	major: number;
	releases: UpstreamRelease[];
	publishedSet: Set<string>;
	syncedAt: string | null;
	loading: boolean;
	canPublish: boolean;
}

export function UpstreamCatalogTable({
	major,
	releases,
	publishedSet,
	syncedAt,
	loading,
	canPublish,
}: Props) {
	const queryClient = useQueryClient();
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "version", desc: true },
	]);

	const upstream = useMemo(
		() => releases.filter((r) => r.browserMajor === major && r.hasX64Deb),
		[releases, major],
	);

	const sync = useMutation({
		mutationFn: async () => {
			const res = await fetch("/api/releases/chromium/sync-now", {
				method: "POST",
				credentials: "same-origin",
			});
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data?.error?.message ?? `sync ${res.status}`);
			}
			return data;
		},
		onSuccess: () => {
			toast.success("Catalog synced");
			queryClient.invalidateQueries({ queryKey: ["chromium-upstream"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Sync failed"),
	});

	const columns = useMemo<ColumnDef<UpstreamRelease>[]>(
		() => [
			{
				id: "version",
				accessorKey: "fullVersion",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Version" />
				),
				cell: ({ row }) => (
					<span className="font-mono text-xs">{row.original.fullVersion}</span>
				),
				sortingFn: (a, b) =>
					compareSemver(a.original.fullVersion, b.original.fullVersion),
				enableHiding: false,
			},
			{
				id: "released",
				accessorKey: "publishedAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Released" />
				),
				cell: ({ row }) => (
					<span className="text-xs text-muted-foreground">
						{formatRelative(row.original.publishedAt)}
					</span>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: ({ row }) =>
					publishedSet.has(row.original.fullVersion) ? (
						<Badge variant="secondary">published</Badge>
					) : (
						<Badge variant="warning">not published</Badge>
					),
				enableSorting: false,
			},
			{
				id: "size",
				header: () => <div className="text-right">Size</div>,
				cell: ({ row }) => {
					const u = row.original;
					const isPublished = publishedSet.has(u.fullVersion);
					return (
						<div className="text-right">
							{isPublished ? (
								<span className="text-xs font-mono tabular-nums">
									{u.assetSizeBytes != null
										? formatBytes(u.assetSizeBytes)
										: "—"}
								</span>
							) : canPublish ? (
								<Button asChild size="sm" variant="outline">
									<Link
										href={`/releases/new/chromium?version=${encodeURIComponent(u.fullVersion)}`}
									>
										Publish
									</Link>
								</Button>
							) : (
								<span className="text-xs text-muted-foreground">—</span>
							)}
						</div>
					);
				},
				enableSorting: false,
				enableHiding: false,
			},
		],
		[publishedSet, canPublish],
	);

	const table = useReactTable({
		data: upstream,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getRowId: (row) => row.fullVersion,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 50 } },
	});

	return (
		<Card>
			<CardHeader>
				<div className="flex items-start justify-between gap-2">
					<div>
						<CardTitle className="text-base">Available upstream</CardTitle>
						<CardDescription>
							{syncedAt
								? `From upstream catalog — last sync ${formatRelative(syncedAt)}`
								: "Catalog not yet synced. Background sync hasn't run successfully yet."}
						</CardDescription>
					</div>
					{canPublish ? (
						<Button
							type="button"
							size="sm"
							variant="ghost"
							onClick={() => sync.mutate()}
							disabled={sync.isPending}
						>
							{sync.isPending ? (
								<Loader2 className="mr-1 size-3.5 animate-spin" />
							) : (
								<RefreshCcw className={cn("mr-1 size-3.5")} />
							)}
							Sync now
						</Button>
					) : null}
				</div>
			</CardHeader>
			<CardContent>
				{loading ? (
					<Skeleton className="h-24 w-full" />
				) : upstream.length === 0 ? (
					<div className="text-sm text-muted-foreground">
						No upstream releases for this major.
					</div>
				) : (
					<DataTable table={table} />
				)}
			</CardContent>
		</Card>
	);
}
